/**
 * SearchTrigger
 *
 * The button that opens the {@link SearchPalette}. It is intentionally separate
 * from the palette itself so a single palette instance (one overlay + one
 * document-level ⌘K listener) can be opened from more than one place — e.g. a
 * desktop nav bar and a mobile top bar — without duplicating the overlay or the
 * keyboard listener.
 *
 * `SearchPalette` renders this internally in its default (self-contained) mode;
 * callers using the palette's `:trigger` block render their own instances and
 * wire them to the yielded `open` action.
 */

import Button from 'tio-ui/components/button';
import { PhMagnifyingGlass } from 'ember-phosphor-icons';
import { type TOC } from '@ember/component/template-only';
import type { ButtonAppearance, ButtonIntent, ButtonSize } from 'tio-ui/components/button';

export interface SearchTriggerSignature {
  Element: HTMLButtonElement;
  Args: {
    /** Accessible label (also the visible label when `showLabel` is true) */
    label: string;
    /** Visual intent for the underlying button. Defaults to the button's own default. */
    intent?: ButtonIntent;
    /** Appearance variant for the underlying button */
    appearance?: ButtonAppearance;
    /** Size for the underlying button */
    size?: ButtonSize;
    /** Whether to render the label text beside the icon */
    showLabel?: boolean;
    /** Whether to render the Cmd/Ctrl-K shortcut hint */
    showShortcutHint?: boolean;
    /** The shortcut hint to display (e.g. "⌘K"). Required when `showShortcutHint` is true. */
    shortcutHint?: string;
    /** Optional title attribute; defaults to the label */
    title?: string;
  };
}

const SearchTrigger: TOC<SearchTriggerSignature> = <template>
  <Button
    @intent={{@intent}}
    @appearance={{@appearance}}
    @size={{@size}}
    @ariaLabel={{@label}}
    aria-keyshortcuts="Meta+K Control+K"
    title={{if @title @title @label}}
    ...attributes
  >
    <PhMagnifyingGlass />
    {{#if @showLabel}}
      <span>{{@label}}</span>
    {{/if}}
    {{#if (and @showShortcutHint @shortcutHint)}}
      <kbd class="tio-search-palette__shortcut">{{@shortcutHint}}</kbd>
    {{/if}}
  </Button>
</template>;

export default SearchTrigger;
